import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { Table2, ExternalLink, ChevronDown, X as XIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTheme } from '../../context/ThemeContext';
import { ModalOverlay, ModalContent, ModalHeader, ModalFooter } from '../ModalStyles';
import { StandaloneVizFrame, openVizListInNewTab } from './StandaloneVizFrame';
import { VizShareLinks } from './VizShareLinks';
import standaloneVizService, { vizType } from '../../api/services/standaloneVizService';

/**
 * Submissions-list launcher for ListView standalone visualizations. Renders a "List visualization" button that
 * opens the chosen list viz over the page's CURRENT filter (the `filterOverride`) in a modal — the launch-time
 * override path. One list viz opens directly; several show a picker popover. Renders nothing when the form has
 * no list vizzes (or the feature isn't deployed).
 *
 * @param {{ formSchemaId: number, filterOverride?: object }} props
 */
export function StandaloneVizListLauncher({ formSchemaId, filterOverride }) {
  const theme = useTheme();
  const [vizList, setVizList] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalViz, setModalViz] = useState(null);
  const [mergedOverride, setMergedOverride] = useState(null);
  const [opening, setOpening] = useState(false);
  const launcherRef = useRef(null);

  const load = useCallback(async () => {
    if (!formSchemaId) return;
    try {
      const list = await standaloneVizService.listVisualizations(formSchemaId);
      setVizList((Array.isArray(list) ? list : []).filter((v) => vizType(v) === 'list'));
    } catch {
      setVizList([]); // feature not deployed / no access → hide quietly
    }
  }, [formSchemaId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e) => {
      if (launcherRef.current && !launcherRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  // When a viz is opened, fetch its SAVED config and merge the live page filter on top. The render endpoint
  // REPLACES the saved config with whatever override it receives, so sending the live filter alone would drop
  // the saved "Show top N" limit and column selections (the TASK-0243 bug). Merging keeps both: saved limit +
  // columns, live field-filter. Falls back to the live filter alone if the saved config can't be fetched.
  useEffect(() => {
    if (!modalViz) { setMergedOverride(null); return undefined; }
    let cancelled = false;
    setMergedOverride(null);
    const live = filterOverride || {};
    standaloneVizService
      .getListConfig(formSchemaId, modalViz.id)
      .then((saved) => {
        if (cancelled) return;
        setMergedOverride({
          ...(saved || {}),
          filters: live.filters ?? [],
          filterLogic: live.filterLogic ?? 'and',
          includeInactive: live.includeInactive ?? false,
        });
      })
      .catch(() => { if (!cancelled) setMergedOverride(live); });
    return () => { cancelled = true; };
  }, [modalViz, formSchemaId, filterOverride]);

  // Share API for the in-modal link control: mint a public link over the SAME effective config the modal is
  // previewing (saved config + live filter merge), so the shared page reproduces what the author sees.
  // NOTE: declared BEFORE any early return so the hook order is stable across renders.
  const shareApi = useMemo(() => {
    if (!modalViz) return null;
    return {
      list: () => standaloneVizService.listListShares(formSchemaId, modalViz.id),
      create: (days) => standaloneVizService.createListShare(formSchemaId, modalViz.id, mergedOverride || filterOverride || null, days),
      revoke: (id) => standaloneVizService.revokeListShare(id),
    };
  }, [formSchemaId, modalViz, mergedOverride, filterOverride]);

  if (vizList.length === 0) return null;

  const openModalFor = (viz) => { setMenuOpen(false); setModalViz(viz); };
  const onLaunchClick = () => {
    if (vizList.length === 1) openModalFor(vizList[0]);
    else setMenuOpen((o) => !o);
  };

  const handleOpenNewTab = async () => {
    if (!modalViz || opening) return;
    setOpening(true);
    try {
      await openVizListInNewTab(formSchemaId, modalViz.id, mergedOverride || filterOverride);
    } catch {
      toast.error('Could not open the visualization.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <>
      <Launcher ref={launcherRef}>
        <LaunchBtn theme={theme} type="button" onClick={onLaunchClick}>
          <Table2 size={14} />
          List visualization
          {vizList.length > 1 && <ChevronDown size={14} />}
        </LaunchBtn>
        {menuOpen && vizList.length > 1 && (
          <Menu theme={theme} role="menu">
            {vizList.map((v) => (
              <MenuItem key={v.id} theme={theme} type="button" role="menuitem" onClick={() => openModalFor(v)}>
                {v.name}
                {v.isDefault ? <DefaultTag theme={theme}>Default</DefaultTag> : null}
              </MenuItem>
            ))}
          </Menu>
        )}
      </Launcher>

      {modalViz && (
        <ModalOverlay theme={theme} $isOpen onClick={() => setModalViz(null)}>
          <VizModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
            <VizModalHeader theme={theme}>
              <Title theme={theme}>
                <Table2 size={16} />
                {modalViz.name}
              </Title>
              <CloseBtn theme={theme} type="button" onClick={() => setModalViz(null)} aria-label="Close">
                <XIcon size={18} />
              </CloseBtn>
            </VizModalHeader>

            <VizModalBody theme={theme}>
              <StandaloneVizFrame
                mode="list"
                formSchemaId={formSchemaId}
                vizId={modalViz.id}
                filterOverride={mergedOverride}
                fitContent
                minHeight={400}
                title={modalViz.name}
              />
            </VizModalBody>

            <VizModalFooter theme={theme}>
              {shareApi && <VizShareLinks api={shareApi} />}
              <OpenBtn theme={theme} type="button" onClick={handleOpenNewTab} disabled={opening}>
                <ExternalLink size={14} /> {opening ? 'Opening…' : 'Open in new tab'}
              </OpenBtn>
            </VizModalFooter>
          </VizModalContent>
        </ModalOverlay>
      )}
    </>
  );
}

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.xs};
  font-weight: 600;
  font-size: 15px;
  color: ${p => p.theme.colors.text};
  min-width: 0;
`;

const Launcher = styled.div`
  position: relative;
  display: inline-block;
`;

const LaunchBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.primary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { border-color: ${p => p.theme.colors.primary}; }
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 200px;
  max-width: 280px;
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  box-shadow: ${p => p.theme.shadows.lg};
  padding: 4px;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${p => p.theme.spacing.sm};
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border: none;
  background: none;
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: 13px;
  color: ${p => p.theme.colors.text};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover { background: ${p => p.theme.colors.backgroundSecondary}; }
`;

const DefaultTag = styled.span`
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  color: ${p => p.theme.colors.tint};
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const VizModalContent = styled(ModalContent)`
  max-width: 1100px;
  width: 95vw;
  height: 85vh;
  max-height: 85vh;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const VizModalHeader = styled(ModalHeader)`
  padding: ${p => p.theme.spacing.lg} ${p => p.theme.spacing.lg} 0;
  margin-bottom: ${p => p.theme.spacing.md};
`;

const VizModalBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 ${p => p.theme.spacing.lg};
`;

const VizModalFooter = styled(ModalFooter)`
  padding: 0 ${p => p.theme.spacing.lg} ${p => p.theme.spacing.lg};
  margin-top: ${p => p.theme.spacing.md};
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.theme.colors.muted};
  padding: 4px;
  display: inline-flex;
  align-items: center;
  &:hover { color: ${p => p.theme.colors.text}; }
`;

const OpenBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid ${p => p.theme.colors.primary};
  border-radius: ${p => p.theme.borderRadius.md};
  background: ${p => p.theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

export default StandaloneVizListLauncher;
